import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import BillboardSidebar from './BillboardSidebar';
import TwoColumnLayout from '../TwoColumnLayout';
import { extractUrlParams, filterDatesBeforeToday } from '../../utils/billboard';
import { getStatesById, getAreasById, isUserLogged, getCinemaEntities } from '../../reducers';
import CMX from '../../api';
import { CinemaType, MovieType } from '../../types/entities';
import BillboardPageContent from './BillboardPageContent';
import { extractTimeZoneFromIsoDate } from '../../utils/dates';
import { getShowtimeDayTimeLimits, isShowtimeAvailableForDate } from '../../utils/showtimes';
import FavCinemasAddRemove from '../user/FavCinemasAddRemove';
import { getUserFavoriteCinemasIds } from '../../reducers/user';
import { cleanForSlug } from '../../utils/urls';
import ShowtimesAreaSelector from '../common/Showtimes/ShowtimesAreaSelector';
import SidebarPanel from '../SidebarPanel';
import BodyBlocker from '../BodyBlocker';

export interface BillboardDataType {
  cinemas: Array<CinemaType & {
    movies: Array<MovieType & {
      versions: Array<any & {
        sessions: Array<any>;
      }>
    }>;
  }>;
  dates?: Array<string>;
}

interface Props {
  fav?: boolean;
  favCinemasIds?: number[];
  location: any;
  match: any;
  history: any;
  statesById: { [key: number]: any };
  areasById: { [key: number]: any };
  cinemasById: { [key: number]: CinemaType };
}

const BillboardPage = (props: Props) => {
  const [urlParams, setUrlParams] = React.useState(extractUrlParams(props.location.pathname, props.match.params));
  const [isLoading, setIsLoading] = React.useState(false);
  const [area, setArea] = React.useState<{ type: string, id: number, name: string } | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [data, setData] = React.useState<BillboardDataType['cinemas'] | null>(null);
  const [filteredData, setFilteredData] = React.useState<BillboardDataType['cinemas'] | null>(null);
  const [baseUrl, setBaseUrl] = React.useState<string>('');
  const [dates, setDates] = React.useState<string[]>([]);
  const [selectedCinemaId, setSelectedCinemaId] = React.useState<number | null>(null);
  const [showFavCinemasEdit, setShowFavCinemasEdit] = React.useState(false);
  const [showAreaSelector, setShowAreaSelector] = React.useState(false);
  const [formats, setFormats] = React.useState<Array<string>>([]);
  const [languages, setLanguages] = React.useState<Array<string>>([]);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  useEffect(() => {
    const newUrlParams = extractUrlParams(props.location.pathname, props.match.params);
    if (JSON.stringify(newUrlParams) !== JSON.stringify(urlParams)) {
      setUrlParams(newUrlParams);
    }

    if (newUrlParams.date !== selectedDate) {
      setSelectedDate(newUrlParams.date);
    }

    if (newUrlParams.cinema && newUrlParams.cinema.length > 0) {
      const cinemaId = newUrlParams.cinema[0];
      if (selectedCinemaId !== cinemaId) {
        setSelectedCinemaId(cinemaId);
      }
    }
  }, [props.location.pathname, props.match.params]);

  useEffect(() => {
    setBaseUrl(
      props.fav
        ? '/cartelera/mis-cines/'
        : `/cartelera/${props.match.params.areaType}-${props.match.params.area}/${props.match.params.slug}/`
    );
  }, [props.match.params.areaType, props.match.params.area, props.match.params.slug]);

  useEffect(() => {
    if (!urlParams.areaId || !urlParams.areaType || !props.areasById || !props.statesById) {
      setArea(null);
      return;
    }

    const areaEntity = ('area' === urlParams.areaType ? props.areasById : props.statesById)[urlParams.areaId];
    const areaSelectionChanged = !area || (area && areaEntity && (area.id !== areaEntity.id || area.type !== urlParams.areaType));
    if (areaEntity && areaSelectionChanged) {
      setData(null);
      if (area) {
        setSelectedCinemaId(null);
      }
      setArea({ ...areaEntity, type: urlParams.areaType });
    }
  }, [urlParams, props.areasById, props.statesById]);

  useEffect(() => {
    if (area || props.fav) {
      setData(null);
      setFilteredData(null);
      fetchAreaBillboard();
    }
  }, [area, props.fav]);

  useEffect(() => {
    if (selectedDate && (area || props.fav)) {
      setData(null);
      setFilteredData(null);
      fetchAreaBillboard();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!dates || !data || dates.length === 0) {
      return;
    }

    if (!selectedDate) {
      setSelectedDate(dates[0]);
    }

    if (selectedDate && data && data.length > 0) {
      let filtered = data;
      filtered = selectedCinemaId ? filtered.filter(cinema => cinema.id === selectedCinemaId) : filtered;
      filtered = removeEverythingOutsideDateLimits(filtered, selectedDate);
      filtered = removeEverythingOutsideHourLimits(filtered, urlParams.hours);      
      filtered = removeEverythingNotMatchingFilters(filtered, urlParams.format, urlParams.lang);

      setFilteredData(filtered);
    }
  }, [data, dates, selectedCinemaId, urlParams.hours, urlParams.format, urlParams.lang]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!selectedCinemaId && data.length > 0) {
      setSelectedCinemaId(data.sort((a, b) => a.name.localeCompare(b.name))[0].id);
    }
  }, [data]);

  const fetchAreaBillboard = () => {
    let endpoint: string | null = null;
    const queryParams = selectedDate ? { date: selectedDate, initial: 1 } : { initial: 1 };

    if (props.fav) {
      endpoint = '/me/cinemas/movies/sessions';
    } else if (area) {
      endpoint = '/cinemas/' + area.type + '/' + area.id + '/movies/';
    }

    if (!endpoint) {
      return;
    }

    setIsLoading(true);
    CMX.get(endpoint, queryParams)
      .then((response: any) => {
        setFilteredData([]);
        setDates(('dates' in response) ? filterDatesBeforeToday(response.dates) : []);
        setData(('cinemas' in response) ? response.cinemas : null);
        const attributes = extractAttributesFromBillboardData(response.cinemas);
        setLanguages(attributes.filter(attr => attr.startsWith('lang_')));
        setFormats(attributes.filter(attr => !attr.startsWith('lang_')));
      })
      .catch(() => { /* @todo handle error */ })
      .finally(() => setIsLoading(false));
  }

  const onDateSelect = (date: string) => {
    props.history.push(buildUrlWithDate(date));
  };

  const buildUrlWithDate = (dateYmd: string) => {
    return buildUrlWithParamValue('date', 'fecha-' + dateYmd);
  }

  const buildUrlWithParamValue = (param: string, value: string) => {
    const params: { [key: string]: string } = Object.assign({}, props.match.params);
    const paramsList = ['date', 'format', 'lang', 'hours', 'cinema', 'movie'];
    const parts: string[] = [];
    
    params[param] = value;

    paramsList.forEach(paramName => {
      if (paramName in params) {
        parts.push(params[paramName]);
      }
    });

    return baseUrl + parts.filter(part => !!part).join('/');
  }

  if (!urlParams) {
    return null;
  }
  
  return <>
    <TwoColumnLayout
      MainContent={BillboardPageContent}
      SidebarContent={BillboardSidebar}
      fixedSidebar
      sidebarMobileToggle
      sidebarToggleNoButton
      sidebarOpen={sidebarOpen}
      sidebarAreaSelector={false}
      sidebarCinemaSelector={false}
      noSidePadding={true}
      {...props}
      baseUrl={baseUrl}
      area={area}
      data={data}
      filteredData={filteredData}
      urlParams={urlParams}
      selectedDate={selectedDate}
      selectedCinemaId={selectedCinemaId}
      dates={dates}
      filters={formats}
      isLoading={isLoading}
      languages={languages}
      onDateSelect={onDateSelect}
      onAreaChangeClick={() => setShowAreaSelector(true)}
      onCinemaSelect={(cinemaId: number) => props.history.push(buildUrlWithParamValue('cinema', 'cine-' + cinemaId))}
      onCinemasEdit={() => setShowFavCinemasEdit(true)}
      onFiltersShowClick={() => setSidebarOpen(true)}
      onFiltersShowClose={() => setSidebarOpen(false)}
      cinemasById={props.cinemasById}
    />
    {showFavCinemasEdit && <FavCinemasAddRemove onClose={() => setShowFavCinemasEdit(false)} />}
    {showAreaSelector && <>
      <BodyBlocker />
      <SidebarPanel overBlocker>
        <ShowtimesAreaSelector
          animate={false}
          cinemaSelect={false}
          title='Seleccionar zona'
          onSave={({ area }) => {
            setShowAreaSelector(false);
            if (area) {
              const areaEntity = ('state' === area.type ? props.statesById : props.areasById)[area.id];
              props.history.push(`/cartelera/${'state' === area.type ? 'estado' : 'zona'}-${area.id}/${cleanForSlug(areaEntity.name)}/`);
            }
          }}
          onClose={() => setShowAreaSelector(false)}
        />
      </SidebarPanel>
    </>}
  </>;
}

const mapStateToProps = (state, ownProps) => {
  return {
    statesById: getStatesById(state),
    areasById: getAreasById(state),
    cinemasById: getCinemaEntities(state),
    favCinemasIds: ownProps.fav ? getUserFavoriteCinemasIds(state) : null,
    isUserLogged: isUserLogged(state),
  }
};

function removeEverythingOutsideDateLimits(data: BillboardDataType['cinemas'], selectedDate?: string): BillboardDataType['cinemas'] {
  if (!selectedDate) {
    return data;
  }

  if (!data || data.length === 0 || !data[0].movies || data[0].movies.length === 0 || !data[0].movies[0].versions || data[0].movies[0].versions.length === 0 || !data[0].movies[0].versions[0].sessions || data[0].movies[0].versions[0].sessions.length === 0) {
    return data;
  }

  const dateLimits = getShowtimeDayTimeLimits(
    selectedDate,
    extractTimeZoneFromIsoDate(data[0].movies[0].versions[0].sessions[0].datetime)
  );

  return data.map(cinema => {
    cinema = Object.assign({}, cinema);
    cinema.movies = cinema.movies.map(movie => {
      movie = Object.assign({}, movie);
      movie.versions = movie.versions.map(version => {
        version = Object.assign({}, version);
        version.sessions = version.sessions ? version.sessions.filter(session => isShowtimeAvailableForDate(new Date(session.datetime).getTime() / 1000, dateLimits)) : [];
        return version;
      }).filter(version => version.sessions && version.sessions.length > 0);
      return movie;
    }).filter(movie => movie.versions.length > 0);
    return cinema;
  }).filter(cinema => cinema.movies.length > 0);
}

function removeEverythingOutsideHourLimits(data: BillboardDataType['cinemas'], hours?: { min: number, max: number }): BillboardDataType['cinemas'] {
  if (!hours) {
    return data;
  }

  return data.map(cinema => {
    return {
      ...cinema,
      movies: cinema.movies.map(movie => {
        return {
          ...movie,
          versions: movie.versions.map(version => {
            return {
              ...version,
              sessions: (version.sessions || []).filter(session => {
                if (!session || !session.datetime || session.datetime.indexOf('T') === -1) {
                  return false;
                }
                const hourPart = session.datetime.split('T')[1].split(':')[0];
                const hour = parseInt(hourPart, 10);
                if (isNaN(hour)) {
                  return false;
                }
                return (hours.min <= hour && hour <= hours.max) || (hour === 0 && hours.max === 24);
              })
            };
          }).filter(version => version.sessions && version.sessions.length > 0)
        };
      }).filter(movie => movie.versions.length > 0)
    };
  }).filter(cinema => cinema.movies.length > 0);
}

function removeEverythingNotMatchingFilters(data: BillboardDataType['cinemas'], formats: string[], langs: string[]): BillboardDataType['cinemas'] {
  let filters: string[] = [];

  if (formats && formats.length) {
    filters = filters.concat(formats);
  }

  if (langs && langs.length) {
    filters = filters.concat(langs);
  }

  if (!filters.length) {
    return data;
  }

  return data.map(cinema => {
    return {
      ...cinema,
      movies: cinema.movies.map(movie => {
        return {
          ...movie,
          versions: movie.versions.filter(version => {
            return version.type && filters.some(f => version.type.indexOf(f) !== -1);
          })
        };
      }).filter(movie => movie.versions && movie.versions.length > 0)
    };
  }).filter(cinema => cinema.movies && cinema.movies.length > 0);
}

function extractAttributesFromBillboardData(cinemas: BillboardDataType['cinemas'] | null | undefined): string[] {
  let result: string[] = [];

  if (!cinemas || cinemas.length === 0) {
    return [];
  }

  cinemas.forEach(cinema => {
    if (!cinema || !cinema.movies || cinema.movies.length === 0) {
      return;
    }

    cinema.movies.forEach(movie => {
      if (!movie || !movie.versions || movie.versions.length === 0) {
        return;
      }

      movie.versions.forEach(version => {
        if (!version || !version.type || !Array.isArray(version.type)) {
          return;
        }

        result = result.concat(version.type)
      });
    });
  });

  const allTypes = Array.from(new Set(result)).sort();

  return allTypes;
}

export default connect(
    mapStateToProps
)(BillboardPage);